<template>
  <v-container fluid>
    <v-card class="pa-4 pt-3">
      <!-- Días de la semana -->
      <v-row class="text-center ma-0">
        <v-col
          v-for="day in weekDays"
          :key="day"
          :cols="12 / 7"
          class="text-caption text-grey-darken-1"
        >
          <v-card-subtitle>{{ day }}</v-card-subtitle>
        </v-col>
      </v-row>

      <!-- Calendario -->
      <div>
        <template v-for="(week, wIdx) in weeks" :key="wIdx">
          <v-row no-gutters>
            <v-col
              v-for="day in week"
              :key="day.date"
              :cols="12 / 7"
              class="pa-1 border"
              :class="{
                'today-overlay': isToday(day.date),
                'outside-month': day.isOutsideMonth
              }"
              style="min-height: 100px;"
              @mousedown="startRangeSelection(day)"
              @mouseup="endRangeSelection(day)"
              @click="handleSingleClick(day)"
            >
              <div class="text-caption font-weight-bold">{{ day.day }}</div>
              <div v-if="events.length">
                <v-chip
                  v-for="event in getEventsForDate(day.date)"
                  :key="event.id"
                  :color="event.color || 'background: rgb(0 0 0 / 20%);'"
                  size="x-small"
                  class="ma-1"
                  @click.stop="() => onView?.(event)"
                  @mousedown.stop="() => onView?.(event)"
                  @mouseup.stop="() => onView?.(event)"
                >
                  {{ formatTime(event.date) }} - {{ event?.original?.subject?.attributes?.commercial_name || event?.original?.subject?.attributes?.legal_name || event?.original?.subject?.attributes?.name  }}
                </v-chip>
              </div>
            </v-col>
          </v-row>
        </template>
      </div>
    </v-card>
  </v-container>
</template>

<script lang="ts">
import { defineComponent, computed, ref, PropType } from 'vue';
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  addDays, format, parseISO, isValid, getMonth
} from 'date-fns';

type CalEvent = {
  id: number;
  date: string;
  name: string;
  color?: string;
  original?: any;
};

type CalendarConfig = {
  dateField: string;
  labelField: string;
  colorField?: string;
};

export default defineComponent({
  name: 'CalendarMonth',
  props: {
    baseDate: { type: Date, required: true },
    events: { type: Array as PropType<CalEvent[]>, required: true },
    title: { type: Object as PropType<{ singular: string }>, required: false },
    moduleName: { type: String, required: false },
    onCreate: { type: Function as PropType<(data: { date: string | [string, string] }) => void>, required: false },
    onView: { type: Function as PropType<(event: any) => void>, required: false },
    calendarConfig: { type: Object as PropType<CalendarConfig>, required: true },
  },
  setup(props) {
    const weekDays = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

    const currentDate = computed(() => new Date(props.baseDate));
    const currentMonth = computed(() => currentDate.value.getMonth());

    const isToday = (dateStr: string) => format(new Date(), 'yyyy-MM-dd') === dateStr;
    const formatTime = (datetime: string) => format(new Date(datetime), 'HH:mm');

    function normalizeDate(input: string): string {
      const date = parseISO(input);
      return isValid(date) ? format(date, 'yyyy-MM-dd') : '';
    }

    const calendarDays = computed(() => {
      const start = startOfWeek(startOfMonth(currentDate.value), { weekStartsOn: 1 });
      const end = endOfWeek(endOfMonth(currentDate.value), { weekStartsOn: 1 });
      const days: Array<{ date: string; day: string; isOutsideMonth: boolean }> = [];

      for (let d = start; d <= end; d = addDays(d, 1)) {
        const dateObj = new Date(d);
        days.push({
          date: format(dateObj, 'yyyy-MM-dd'),
          day: format(dateObj, 'd'),
          isOutsideMonth: getMonth(dateObj) !== currentMonth.value
        });
      }
      return days;
    });

    const weeks = computed(() => {
      const days = calendarDays.value;
      const chunks: typeof calendarDays.value[] = [];
      for (let i = 0; i < days.length; i += 7) {
        chunks.push(days.slice(i, i + 7));
      }
      return chunks;
    });

    const selectedRange = ref<{ start: string | null; end: string | null }>({ start: null, end: null });
    let clickTimeout: ReturnType<typeof setTimeout> | null = null;

    function startRangeSelection(day: { date: string }) {
      selectedRange.value.start = day.date;
      selectedRange.value.end = null;
    }

    function endRangeSelection(day: { date: string }) {
      if (!selectedRange.value.start) return;
      selectedRange.value.end = day.date;

      if (selectedRange.value.start > selectedRange.value.end) {
        [selectedRange.value.start, selectedRange.value.end] = [selectedRange.value.end, selectedRange.value.start];
      }

      props.onCreate?.({ date: [selectedRange.value.start, selectedRange.value.end] });

      selectedRange.value.start = null;
      selectedRange.value.end = null;
    }

    function handleSingleClick(day: { date: string }) {
      if (clickTimeout) clearTimeout(clickTimeout);
      clickTimeout = setTimeout(() => {
        props.onCreate?.({ date: day.date });
      }, 200);
    }

    const getEventsForDate = (dateStr: string) => {
      const field = props.calendarConfig?.dateField || 'date';
      return props.events
        .filter(e => normalizeDate(e.original?.[field]) === dateStr)
        .sort(
          (a, b) =>
            new Date(a.original?.[field]).getTime() -
            new Date(b.original?.[field]).getTime()
        );
    };

    return {
      // template utils
      format,
      formatTime,
      isToday,

      // data
      weekDays,
      weeks,

      // methods
      startRangeSelection,
      endRangeSelection,
      handleSingleClick,
      getEventsForDate,

      // props direct (si los necesitas en template)
      events: props.events,
      onView: props.onView,
    };
  },
});
</script>

<style scoped>
.outside-month {
  opacity: 0.4;
}
.today-overlay {
  background: rgb(0 0 0 / 15%);
}
</style>
